import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  FIRST_AID_KITS,
  FIRST_AID_SCENARIOS,
  FLARES,
  REEFING_BANDS,
  STORM_TACTICS,
  TRS_ZONES,
  type ReefingBand,
} from "@nav-games/safety";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface SafetyProps {
  onNavigate: (to: string) => void;
}

const MODULE = "safety";
const GAME = "scenarios";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

type Kind = "flare" | "kit" | "first-aid" | "storm-tactic" | "reef" | "trs";

interface Card {
  kind: Kind;
  prompt: string;
  answerId: string;
  choices: ReadonlyArray<{ id: string; label: string }>;
  reveal: string;
}

const FLARE_SCENARIOS: ReadonlyArray<{ id: string; prompt: string }> = [
  {
    id: "parachute-red",
    prompt:
      "You've made a Mayday call and the Coastguard has acknowledged. Lifeboat is being launched but is 20 nm away. Which flare do you fire FIRST to give them a bearing?",
  },
  {
    id: "parachute-red",
    prompt:
      "Night, offshore, drifting after a dismasting. You want to alert any vessel within sight + give SAR a fix at long range. Best initial flare?",
  },
  {
    id: "handheld-red",
    prompt:
      "Helicopter is over your position; you can hear the rotors but they haven't seen you. Which flare to mark your exact position right now?",
  },
  {
    id: "handheld-red",
    prompt:
      "Lifeboat is closing on you ~1 nm out. You want to pinpoint yourself in the swell. Best flare?",
  },
  {
    id: "smoke-orange",
    prompt:
      "Bright midday sun. Helicopter is searching for you. Which signal works best in this light?",
  },
  {
    id: "smoke-orange",
    prompt:
      "Day, in a liferaft, you spot a yacht 1 nm away that hasn't seen you. Best day-signal?",
  },
  {
    id: "handheld-white",
    prompt:
      "Night, moored quietly. A motor cruiser is heading straight for you under autopilot, no lookout. Which flare do you fire?",
  },
  {
    id: "led-electronic",
    prompt:
      "You're a coastal day-sailer and want a non-expiring distress signal that's safer to handle. What's the modern alternative to a handheld red?",
  },
];

const KIT_SCENARIOS: ReadonlyArray<{ id: string; prompt: string }> = [
  {
    id: "D",
    prompt: "Day-sail to the next harbour ~10 nm away, returning the same day. Which first-aid kit category fits the voyage?",
  },
  {
    id: "C",
    prompt: "Weekend coastal cruise, 30 nm offshore at the furthest point. What category of first-aid kit?",
  },
  {
    id: "C",
    prompt: "RYA-recommended baseline for any coastal cruising up to 60 nm from a safe haven?",
  },
  {
    id: "B",
    prompt: "Cross-Channel + Bay of Biscay passage, 60–150 nm offshore. Which kit category?",
  },
  {
    id: "A",
    prompt: "Atlantic crossing — 2000 nm offshore, SAR potentially days away. Which kit category?",
  },
];

const FIRST_AID_SCENARIO_PROMPTS: ReadonlyArray<{
  id: string;
  prompt: string;
}> = [
  {
    id: "cold-water-shock",
    prompt:
      "Casualty just fallen in. They're gasping uncontrollably and trying to swim for the boat. What's the immediate priority?",
  },
  {
    id: "hypothermia",
    prompt:
      "Crew member rescued from the water 20 minutes ago. Shivering hard, slurring words, fumbling with zips. What condition + what action?",
  },
  {
    id: "seasickness",
    prompt:
      "Day 2 of a passage. New crew member pale, sweating, repeatedly vomiting + can't keep water down. What action?",
  },
  {
    id: "drowning",
    prompt:
      "MOB recovered. They've coughed up water + seem OK now. They want to go below + sleep it off. Should you let them?",
  },
  {
    id: "burn-galley",
    prompt:
      "Kettle of boiling water has spilled across crew's arm. Skin red + blistered. First action?",
  },
  {
    id: "fracture",
    prompt:
      "Crew member fell heavily on a wet deck + their forearm is deformed + painful. They want you to 'pull it straight'. Should you?",
  },
];

function shuffled<T>(arr: readonly T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = out[i]!;
    out[i] = out[j]!;
    out[j] = a;
  }
  return out;
}

const STORM_TACTIC_SCENARIOS: ReadonlyArray<{ id: string; prompt: string }> = [
  {
    id: "heaving-to",
    prompt:
      "F8 sustained, large but regular Atlantic swell, well clear of any lee shore. Crew tired, need to rest + eat. Which tactic?",
  },
  {
    id: "heaving-to",
    prompt:
      "Galley needs attention + you want the boat stopped without anchoring + the wind is moderate-to-strong but not survival-grade. Which tactic?",
  },
  {
    id: "fore-reaching",
    prompt:
      "F7 with breaking seas, sea room to weather + a lee shore 30 nm downwind. You need to hold ground to windward. Which tactic?",
  },
  {
    id: "running-off",
    prompt:
      "Deep depression with F9 winds + huge following seas + sea room downwind for hundreds of miles. Crew tired but functional. Which tactic?",
  },
  {
    id: "drogue",
    prompt:
      "F10 conditions, running off but yawing badly + crew fear of broaching. What do you stream from the stern?",
  },
  {
    id: "sea-anchor",
    prompt:
      "Multihull in a TRS-grade blow needing to maintain position with minimal drift + keep the bow into the seas. Which tactic?",
  },
  {
    id: "lying-ahull",
    prompt:
      "All sails dropped, helm lashed, no active steering, beam-on to wind + sea. Quickest tactic to deploy but most exposed. Which tactic?",
  },
];

const REEF_SCENARIOS: ReadonlyArray<{ state: ReefingBand["state"]; prompt: string }> = [
  {
    state: "full-sail",
    prompt: "Steady F3 with no forecast change. 40 ft cruiser — sail plan?",
  },
  {
    state: "1st-reef",
    prompt: "Wind has built from F3 to a steady F5, gusts F6. Heel approaching 25° at the gusts. What's the right reef?",
  },
  {
    state: "2nd-reef",
    prompt: "F6 sustained with gusts F7, weather helm noticeable, jib over-reefed via the furler. What configuration?",
  },
  {
    state: "3rd-reef",
    prompt: "F7-F8, water over the bow regularly, crew in lifejackets + tethered. Sail plan?",
  },
  {
    state: "trysail-storm-jib",
    prompt: "Sustained F9 in open ocean, 3rd-reef main flogging dangerously in the gusts, boom looking unsafe. Switch to what?",
  },
];

const TRS_SCENARIOS: ReadonlyArray<{ id: string; prompt: string }> = [
  {
    id: "dangerous",
    prompt: "Northern hemisphere TRS approaching. You're north + west of the forecast track. Which semicircle?",
  },
  {
    id: "navigable",
    prompt: "Northern hemisphere TRS approaching. You're south + east of the forecast track. Which semicircle?",
  },
  {
    id: "dangerous",
    prompt: "Southern hemisphere TRS approaching. You're south + west of the forecast track. Which semicircle?",
  },
  {
    id: "navigable",
    prompt: "Southern hemisphere TRS approaching. You're north + east of the forecast track. Which semicircle?",
  },
];

function buildCard(): Card {
  const kinds: Kind[] = ["flare", "kit", "first-aid", "storm-tactic", "reef", "trs"];
  const kind = kinds[Math.floor(Math.random() * kinds.length)]!;

  if (kind === "flare") {
    const sc =
      FLARE_SCENARIOS[Math.floor(Math.random() * FLARE_SCENARIOS.length)]!;
    const answer = FLARES.find((f) => f.id === sc.id)!;
    const choices = shuffled(
      FLARES.map((f) => ({ id: f.id, label: f.name })),
    ).slice(0, CHOICES_PER_CARD);
    if (!choices.find((c) => c.id === answer.id))
      choices[0] = { id: answer.id, label: answer.name };
    return {
      kind,
      prompt: sc.prompt,
      answerId: answer.id,
      choices: shuffled(choices),
      reveal: `${answer.summary} ${answer.use}`,
    };
  }

  if (kind === "kit") {
    const sc = KIT_SCENARIOS[Math.floor(Math.random() * KIT_SCENARIOS.length)]!;
    const answer = FIRST_AID_KITS.find((k) => k.category === sc.id)!;
    const choices = shuffled(
      FIRST_AID_KITS.map((k) => ({
        id: k.category,
        label: `Cat ${k.category} — ${k.scope}`,
      })),
    );
    return {
      kind,
      prompt: sc.prompt,
      answerId: answer.category,
      choices,
      reveal: `${answer.summary} (${answer.distance})`,
    };
  }

  if (kind === "storm-tactic") {
    const sc =
      STORM_TACTIC_SCENARIOS[
        Math.floor(Math.random() * STORM_TACTIC_SCENARIOS.length)
      ]!;
    const answer = STORM_TACTICS.find((t) => t.id === sc.id)!;
    const choices = shuffled(
      STORM_TACTICS.map((t) => ({ id: t.id, label: t.name })),
    ).slice(0, CHOICES_PER_CARD);
    if (!choices.find((c) => c.id === answer.id))
      choices[0] = { id: answer.id, label: answer.name };
    return {
      kind,
      prompt: sc.prompt,
      answerId: answer.id,
      choices: shuffled(choices),
      reveal: `${answer.summary} ${answer.behaviour}`,
    };
  }

  if (kind === "reef") {
    const sc =
      REEF_SCENARIOS[Math.floor(Math.random() * REEF_SCENARIOS.length)]!;
    const answer = REEFING_BANDS.find((r) => r.state === sc.state)!;
    const choices = shuffled(
      REEFING_BANDS.map((r) => ({
        id: r.state,
        label: r.configuration,
      })),
    ).slice(0, CHOICES_PER_CARD);
    if (!choices.find((c) => c.id === answer.state))
      choices[0] = { id: answer.state, label: answer.configuration };
    return {
      kind,
      prompt: sc.prompt,
      answerId: answer.state,
      choices: shuffled(choices),
      reveal: `${answer.windKn} · ${answer.beaufort}. ${answer.decision}`,
    };
  }

  if (kind === "trs") {
    const sc = TRS_SCENARIOS[Math.floor(Math.random() * TRS_SCENARIOS.length)]!;
    const answer = TRS_ZONES.find((z) => z.id === sc.id)!;
    const choices = TRS_ZONES.map((z) => ({ id: z.id, label: z.name }));
    return {
      kind,
      prompt: sc.prompt,
      answerId: answer.id,
      choices: shuffled(choices),
      reveal: `${answer.position} ${answer.escape}`,
    };
  }

  const sc =
    FIRST_AID_SCENARIO_PROMPTS[
      Math.floor(Math.random() * FIRST_AID_SCENARIO_PROMPTS.length)
    ]!;
  const answer = FIRST_AID_SCENARIOS.find((s) => s.id === sc.id)!;
  const choices = shuffled(
    FIRST_AID_SCENARIOS.map((s) => ({ id: s.id, label: s.name })),
  ).slice(0, CHOICES_PER_CARD);
  if (!choices.find((c) => c.id === answer.id))
    choices[0] = { id: answer.id, label: answer.name };
  return {
    kind,
    prompt: sc.prompt,
    answerId: answer.id,
    choices: shuffled(choices),
    reveal: `${answer.summary} ${answer.action}`,
  };
}

function topicHeading(kind: Kind): string {
  if (kind === "flare") return "Which flare?";
  if (kind === "kit") return "Which first-aid kit?";
  if (kind === "storm-tactic") return "Which storm tactic?";
  if (kind === "reef") return "Which sail configuration?";
  if (kind === "trs") return "Which TRS semicircle?";
  return "What is it + what do you do?";
}

export function Safety({ onNavigate }: SafetyProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (id: string) => {
      if (chosen || result) return;
      const ok = id === card.answerId;
      const attempt: Omit<Attempt, "module"> = {
        char: `${card.kind}-${card.answerId}`,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(id);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/reference")}
      />
    );
  }

  const revealed = chosen !== null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "safety" : null} onClose={() => setHelpOpen(false)} />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION} · safety + first aid
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{topicHeading(card.kind)}</h3>
        <p style={{ marginBottom: 12 }}>{card.prompt}</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c.id === card.answerId;
            const isChosen = c.id === chosen;
            return (
              <button
                key={c.id}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c.id)}
                style={{
                  fontSize: 14,
                  padding: "12px 6px",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.answerId
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.answerId ? "✓ Correct." : "✗ Not quite."}
            </p>
            <p className="small" style={{ margin: "8px 0 0" }}>
              {card.reveal}
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/reference")}>
        ← Back to Reference
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Reference
        </button>
      </div>
    </>
  );
}
